import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ConfidenceFactor } from '@dill/dill-shared';
import { ConfidenceFactorList } from './ConfidenceFactorList';

interface ConfidenceBadgeProps {
  confidence: number | null;
  showLabel?: boolean;
  /**
   * Why the score is what it is, in the order the factors applied.
   *
   * Optional: records written before the derivation was stored have none, and the badge renders
   * exactly as it always did. When present the last factor's score is the badge's own number.
   */
  factors?: ConfidenceFactor[] | null;
}

/**
 * The derivation, on hover.
 *
 * A confidence number that disagrees with the per-invoice evidence beside it — 67% over eight
 * invoices each marked 100% — teaches an operator to ignore the badge. Showing the two things
 * that decided it, and what each did to the number, is what makes the score answerable rather
 * than something to be worked around.
 *
 * The portal-and-timeout pattern is duplicated from MatchedInvoicesTooltip rather than extracted:
 * three queues already depend on that component, and a shared abstraction here would buy a
 * refactor of it for nothing this needs.
 */
export const ConfidenceBadge = ({
  confidence,
  showLabel = true,
  factors,
}: ConfidenceBadgeProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearHideTimeout();
  }, []);

  /**
   * Pull the panel back inside the viewport once its real height is known.
   *
   * Anchoring to the row's top is right for a row near the top of the table and wrong for the last
   * one, where the panel runs off the bottom of the window and the factors below the fold cannot be
   * reached — the panel is a hover target, so there is nothing to scroll to it.
   *
   * Measured rather than assumed because the height depends on how many factors there are and how
   * far each detail wraps. useLayoutEffect runs before paint, so the corrected position is the
   * first one drawn.
   */
  useLayoutEffect(() => {
    if (!isHovered || !panelRef.current) {
      return;
    }
    const { height } = panelRef.current.getBoundingClientRect();
    const VIEWPORT_MARGIN = 8;
    const lowestTop = window.innerHeight - height - VIEWPORT_MARGIN;
    setPosition((current) => {
      const top = Math.max(VIEWPORT_MARGIN, Math.min(current.top, lowestTop));
      return top === current.top ? current : { ...current, top };
    });
  }, [isHovered]);

  if (confidence === null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        N/A
      </span>
    );
  }

  const percentage = Math.round(confidence * 100);
  let colorClasses = '';
  let label = '';

  if (confidence >= 0.9) {
    colorClasses = 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
    label = 'High';
  } else if (confidence >= 0.7) {
    colorClasses = 'bg-amber-50 text-amber-700 ring-amber-600/20';
    label = 'Medium';
  } else {
    colorClasses = 'bg-red-50 text-red-700 ring-red-600/20';
    label = 'Low';
  }

  const shownFactors = factors ?? [];
  const hasFactors = shownFactors.length > 0;

  const handleMouseEnter = () => {
    clearHideTimeout();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const PANEL_W = 360;
      const left =
        rect.right + PANEL_W + 8 > window.innerWidth ? rect.left - PANEL_W - 4 : rect.right + 8;
      setPosition({ top: rect.top, left: Math.max(left, 8) });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  const badge = (
    <span
      ref={triggerRef}
      onMouseEnter={hasFactors ? handleMouseEnter : undefined}
      onMouseLeave={hasFactors ? handleMouseLeave : undefined}
      className={
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ' +
        colorClasses +
        (hasFactors ? ' cursor-help border-b border-dotted border-current/40' : '')
      }>
      {percentage}%{showLabel && <span className="opacity-75">({label})</span>}
    </span>
  );

  if (!hasFactors) {
    return badge;
  }

  return (
    <>
      {badge}
      {isHovered &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed bg-white border border-gray-200 shadow-lg rounded-xl z-[99999] w-[360px]"
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            onMouseEnter={clearHideTimeout}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Why {percentage}%
              </p>
            </div>
            <div className="max-h-[280px] overflow-y-auto px-3 py-2">
              <ConfidenceFactorList factors={shownFactors} />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
